import { createUniqueId } from './common-util.js';
import {
   FARE_STATUS,
   MAX_LENGTH_FIELDS,
   USER_ACTION_CODE_FARES_SCREEN,
} from './constants.js';
import { DateUtil } from './date-util.js';
import { NOTIFICATION_MESSAGES } from './notification-messages.js';
import { toastWarn } from './toasts.js';
import {
   validateAlphanumericText,
   validateAlphanumericWithUnderscoreText,
   validateAlphaText,
   validateMathOperators,
   validateNumericText,
} from './validations.js';

const getEffectiveDate = () =>
   DateUtil.formatDate(
      new Date(),
      DateUtil.DATE_FORMATS.DD_MMM_YY
   ).toUpperCase();

const handleCellEdit = (
   api,
   columnName,
   newValue,
   updateEffectiveDate = true
) => {
   const selectedNodes = api.getSelectedNodes();

   const rowsToUpdate = selectedNodes
      .filter(
         (node) =>
            node.data.isNewRow || node.data.status === FARE_STATUS.WORKING
      )
      .map((node) => ({
         ...node.data,
         [columnName]: newValue.toUpperCase(),
         isEdited: true,
      }));

   if (rowsToUpdate.length) {
      api.applyTransaction({
         update: rowsToUpdate,
      });
   } else {
      const rowsToAdd = selectedNodes.map((node) => ({
         ...node.data,
         isNewRow: true,
         id: createUniqueId(),
         [columnName]: newValue.toUpperCase(),
         userActionCode: USER_ACTION_CODE_FARES_SCREEN.UPDATE,
         ...(updateEffectiveDate && {
            effectiveDate: getEffectiveDate(),
         }),
         status: '',
      }));

      setTimeout(() => {
         api.applyTransaction({
            add: rowsToAdd,
            addIndex: selectedNodes[0].rowIndex,
         });
      });
   }
};

export const originValueSetter = ({ newValue, api }) => {
   if (!validateAlphaText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length !== MAX_LENGTH_FIELDS.ORIGIN) {
      toastWarn(NOTIFICATION_MESSAGES.THREE_CHARACTERS_ONLY);
      return false;
   }

   handleCellEdit(api, 'origin', newValue);
   return false;
};

export const destinationValueSetter = ({ newValue, api }) => {
   if (!validateAlphaText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length !== MAX_LENGTH_FIELDS.DESTINATION) {
      toastWarn(NOTIFICATION_MESSAGES.THREE_CHARACTERS_ONLY);
      return false;
   }

   handleCellEdit(api, 'destination', newValue);
   return false;
};

export const marketIdValueSetter = ({ newValue, api }) => {
   if (!validateAlphanumericText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.MARKET_ID) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_ID);
      return false;
   }

   handleCellEdit(api, 'marketId', newValue);
   return false;
};

export const fareClassCodeValueSetter = ({ newValue, api, data }) => {
   if (newValue.length > MAX_LENGTH_FIELDS.FARE_CLASS_CODE) {
      toastWarn(NOTIFICATION_MESSAGES.MAX_LENGTH_EIGHT_CHARS);
      return false;
   }

   if (!validateAlphanumericWithUnderscoreText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_CHARACTER);
      return false;
   }

   const referenceValue = data.originalValue || newValue.toUpperCase();

   if (referenceValue.includes('_')) {
      const selectedNodes = api.getSelectedNodes();
      const rowsToUpdate = selectedNodes.filter(
         (node) =>
            node.data.isNewRow || node.data.status === FARE_STATUS.WORKING
      );

      const updatedRows = rowsToUpdate.map((node) => {
         const fareClassCode = node.data.fareClassCode.slice(
            0,
            referenceValue.length
         );

         const updatedFareClassCode = fareClassCode
            .split('')
            .map((_, index) =>
               referenceValue[index] === '_'
                  ? fareClassCode[index]
                  : referenceValue[index]
            )
            .join('')
            .toUpperCase();

         return {
            ...node.data,
            fareClassCode: updatedFareClassCode,
            isEdited: true,
         };
      });

      api.applyTransaction({
         update: updatedRows,
      });
   } else {
      handleCellEdit(api, 'fareClassCode', newValue.toUpperCase());
   }

   return false;
};

export const amountColumnValueSetter = ({ newValue, api }) => {
   if (newValue.length > MAX_LENGTH_FIELDS.AMOUNT) {
      toastWarn(NOTIFICATION_MESSAGES.MAX_LENGTH_EIGHT_CHARS);
      return false;
   }

   if (!validateMathOperators(newValue)) {
      toastWarn(
         NOTIFICATION_MESSAGES.INVALID_MATH_OPERATION_AMOUNT_FIELD_FORMAT
      );
      return false;
   }

   const selectedNodes = api.getSelectedNodes();
   const existingTemporaryRows = selectedNodes.filter(
      (node) => node.data.isNewRow || node.data.status === FARE_STATUS.WORKING
   );
   const rowsToUpdate = existingTemporaryRows.length
      ? existingTemporaryRows
      : selectedNodes;

   const hasMathOperator = ['=*', '=+', '=-', '=/'].some((prefix) =>
      newValue.startsWith(prefix)
   );

   if (hasMathOperator) {
      const mathOperator = newValue.slice(0, 2);
      const value = parseInt(newValue.slice(2));

      const operations = {
         '=*': (a, b) => a * b,
         '=+': (a, b) => a + b,
         '=-': (a, b) => a - b,
         '=/': (a, b) => a / b,
      };

      const updatedRows = rowsToUpdate.map((node) => ({
         ...node.data,
         fareAmount: operations[mathOperator](
            parseInt(node.data.fareAmount),
            value
         ),
         isEdited: true,
      }));

      if (existingTemporaryRows.length) {
         api.applyTransaction({
            update: updatedRows,
         });
      } else {
         const rowsToAdd = updatedRows?.map((data) => ({
            ...data,
            isNewRow: true,
            id: createUniqueId(),
            userActionCode: USER_ACTION_CODE_FARES_SCREEN.UPDATE,
            status: '',
            effectiveDate: getEffectiveDate(),
         }));

         setTimeout(() => {
            api.applyTransaction({
               add: rowsToAdd,
               addIndex: selectedNodes[0].rowIndex,
            });
         });
      }
   } else if (!isNaN(parseInt(newValue))) {
      handleCellEdit(api, 'fareAmount', newValue);
   } else {
      toastWarn(
         NOTIFICATION_MESSAGES.INVALID_MATH_OPERATION_AMOUNT_FIELD_FORMAT
      );
   }

   return false;
};

export const carrierCodeValueSetter = ({ newValue, api }) => {
   if (!validateAlphanumericText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length !== MAX_LENGTH_FIELDS.CARRIER_CODE) {
      toastWarn(NOTIFICATION_MESSAGES.TWO_CHARACTERS_ONLY);
      return false;
   }

   handleCellEdit(api, 'carrierCode', newValue);
   return false;
};

export const tariffNumberValueSetter = ({ newValue, api }) => {
   if (!validateNumericText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.TARIFF_NUMBER) {
      toastWarn(NOTIFICATION_MESSAGES.MAX_LENGTH_THREE_CHARS);
      return false;
   }

   handleCellEdit(api, 'tariffNumber', newValue);
   return false;
};

export const footnoteCodeValueSetter = ({ newValue, api }) => {
   if (!validateAlphanumericText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.FOOTNOTE_CODE) {
      toastWarn(NOTIFICATION_MESSAGES.TWO_CHARACTERS_ONLY);
      return false;
   }

   handleCellEdit(api, 'footnoteCode', newValue);
   return false;
};

export const routingNumberValueSetter = ({ newValue, api }) => {
   if (!validateNumericText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.ROUTING_NUMBER) {
      toastWarn(NOTIFICATION_MESSAGES.MAX_LENGTH_FOUR_CHARS);
      return false;
   }

   handleCellEdit(api, 'routingNumber', newValue);
   return false;
};

export const roundTripIndicatorValueSetter = ({ newValue, api }) => {
   if (!validateNumericText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.ROUND_TRIP_INDICATOR) {
      toastWarn(NOTIFICATION_MESSAGES.ONE_CHARACTER_ONLY);
      return false;
   }

   handleCellEdit(api, 'roundTripIndicator', newValue);
   return false;
};

export const currencyCodeValueSetter = ({ newValue, api }) => {
   if (!validateAlphaText(newValue)) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.CURRENCY_CODE) {
      toastWarn(NOTIFICATION_MESSAGES.THREE_CHARACTERS_ONLY);
      return false;
   }

   handleCellEdit(api, 'currencyCode', newValue);
   return false;
};

export const effectiveDateValueSetter = ({ newValue, api }) => {
   if (!DateUtil.validate(new Date(newValue))) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.EFFECTIVE_DATE) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_DATE_FORMAT);
      return false;
   }

   const valueToUpdate = DateUtil.formatDate(
      newValue,
      DateUtil.DATE_FORMATS.DD_MMM_YY
   ).toUpperCase();
   handleCellEdit(api, 'effectiveDate', valueToUpdate, false);
   return false;
};

export const discontinueDateValueSetter = ({ newValue, api }) => {
   if (!DateUtil.validate(new Date(newValue))) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_INPUT);
      return false;
   }

   if (newValue.length > MAX_LENGTH_FIELDS.DISCONTINUE_DATE) {
      toastWarn(NOTIFICATION_MESSAGES.INVALID_DATE_FORMAT);
      return false;
   }

   const valueToUpdate = DateUtil.formatDate(
      newValue,
      DateUtil.DATE_FORMATS.DD_MMM_YY
   ).toUpperCase();
   handleCellEdit(api, 'discontinueDate', valueToUpdate);
   return false;
};
